import { Card, notification, Button } from 'antd';
import { IBunkerData } from '@/configurator/ts';
import styles from './index.module.sass';
import fakeImage from '@/common/images/placeholder.png';
import { Counter } from '@/configurator/components/shared/counter';
import {
  useBulkAddBunkerMutation,
  useGetPickerDetailsQuery,
  useMarkBunkerAsConfiguredMutation,
  useRemoveBunkerMutation,
  useUpdateQuantityMutation,
} from '@/services/picker.service';
import { ClientStorageService } from '@/common/services/client-storage.service';
import { DiContainer } from '@/di/di-container';
import { registerQuantityFlush } from '@/configurator/helpers/quantity-flush';
import { addSpacesInNumbers } from '@/configurator/helpers/add-spaces-in-numbers';
import { useEffect, useRef, useState } from 'react';

const clientStorageService = DiContainer.get(ClientStorageService);

export interface IBunkerCard {
  idx: number;
  id: number;
  pickerId: number;
  model: string;
  image: string;
  description: string;
  count: number;
  technicalParams: Array<Record<string, string>>;
  setCount?: (id: number, count: number) => void;
  canConfigured?: boolean;
  onConfigure?: () => void;
  price?: number;
}

export function BunkerCard(props: IBunkerCard) {
  const { refetch, isFetching } = useGetPickerDetailsQuery(
    clientStorageService.getUUID()
  );
  const [
    updateQuantity,
    { isLoading: isUpdateQuantityLoading, isError: isUpdateQuantityError },
  ] = useUpdateQuantityMutation();

  const [markBunkerAsConfigured] = useMarkBunkerAsConfiguredMutation();

  const [
    removeBunker,
    { isLoading: isRemoveBunkerLoading, isError: isRemoveBunkerError },
  ] = useRemoveBunkerMutation();

  const [bulkAdd, { isLoading: isBulkAddLoading, isError: isBulkAddError }] =
    useBulkAddBunkerMutation();

  const { model, count, id, pickerId, description } = props;

  // Оптимистичное количество + debounce записи на бэк (без задержки на каждый клик).
  const [localCount, setLocalCount] = useState(count);
  const desiredRef = useRef(count); // что хочет пользователь (мгновенно)
  const syncedRef = useRef(count); // что уже записано на бэк
  const inflightRef = useRef<Promise<void> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const changeCount = async (val: number) => {
    // если picker не существует
    if (val <= 0) {
      await removeBunker({
        id: pickerId,
        session: clientStorageService.getUUID(),
      });
      refetch && (await refetch());
      return;
    }
    if (pickerId === 0) {
      await bulkAdd({
        body: {
          data: [
            {
              bunker_id: id,
              quantity: val,
            },
          ],
        },
        session: clientStorageService.getUUID(),
      });
      refetch && (await refetch());
      return;
    }

    // если picker существует

    !props.canConfigured &&
      (await markBunkerAsConfigured({
        id: pickerId,
        session: clientStorageService.getUUID(),
        configured: false,
      }));

    await updateQuantity({
      id: pickerId,
      quantity: val,
      session: clientStorageService.getUUID(),
    });

    !props.canConfigured &&
      (await markBunkerAsConfigured({
        id: pickerId,
        session: clientStorageService.getUUID(),
        configured: false,
      }));

    refetch && (await refetch());
  };

  // Однопоточная запись на бэк: пишем, пока synced не догонит desired.
  const runSync = async () => {
    while (desiredRef.current !== syncedRef.current) {
      const target = desiredRef.current;
      await changeCount(target);
      syncedRef.current = target;
    }
  };
  const syncToServer = () => {
    if (!inflightRef.current) {
      inflightRef.current = runSync().finally(() => {
        inflightRef.current = null;
      });
    }
    return inflightRef.current;
  };

  const onCountChange = (val: number) => {
    setLocalCount(val); // мгновенно в UI
    desiredRef.current = val;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      void syncToServer();
    }, 500);
  };

  // Флаш — дождаться записи (вызывается перед переходом в опции).
  const flush = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    await syncToServer();
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => registerQuantityFlush(() => flushRef.current()), []);

  // Отображение синхронизируем с сервером, когда нет ни pending, ни активной записи.
  useEffect(() => {
    if (!inflightRef.current && timerRef.current === undefined) {
      setLocalCount(count);
      desiredRef.current = count;
      syncedRef.current = count;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useEffect(() => {
    if (isBulkAddError || isRemoveBunkerError || isUpdateQuantityError) {
      notification.warning({
        message: 'Ошибка при изменении количества. Попробуйте позже',
      });
    }
  }, [isBulkAddError, isRemoveBunkerError, isUpdateQuantityError]);

  return (
    <Card bordered style={{ border: '3px solid #66B28A', minHeight: '550px' }}>
      <div className={styles.model}>{model}</div>
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className={styles.left}>
          <img className={styles.catalogImg} src={props.image || fakeImage} />
        </div>
        <div className={styles.right}>
          {props.technicalParams?.map((param) => {
            return (
              <>
                <span key={param.name} className={styles.info}>
                  {param.display_name}
                </span>
                <p className={styles.data}>{param.value}</p>
              </>
            );
          })}
        </div>
      </div>
      <div className={'overscroll-auto max-h-[200px] overflow-y-auto'}>
        <p className={styles.description}>
          {description
            ? description
            : 'Габаритный бункер в модельной линейке компании. За счет своих размеров он может перемещаться по дорогам общего пользования. Это также первая двухсекционная модель, что позволяет использовать ее на севе для загрузки семенами и удобрениями одновременно и впоследствии быстро загружать сеялки.'}
        </p>
      </div>

      {!!props.price && (
        <div className="my-3 text-[20px] font-semibold text-[#007E3C]">
          от {addSpacesInNumbers(Math.round(props.price))} ₽
        </div>
      )}

      <Counter
        idx={0}
        minValue={0}
        count={localCount}
        setCount={onCountChange}
        loading={false}
      />

      {props.onConfigure && localCount > 0 && (
        <Button
          type="primary"
          block
          className="mt-4"
          style={{ background: '#66B28A' }}
          onClick={props.onConfigure}>
          Конфигурировать опции
        </Button>
      )}
    </Card>
  );
}
